/**
 * Единственное место, где доля превращается в процент со знаком.
 * Контракт §1: drop_ratio ВСЕГДА положителен (0.22 = подешевело на 22%),
 * форматирование знака — задача интерфейса. Умножать на -100 где-либо ещё
 * запрещено (Global Constraint 8).
 */

const MINUS = '−'; // типографский минус, а не дефис: в tabular-nums он одной ширины с цифрой

const rubles = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function formatMoney(amount: number, currency = 'RUB'): string {
  const number = rubles.format(Math.round(amount));
  return currency === 'RUB' ? `${number} ₽` : `${number} ${currency}`;
}

/** 0.22 → «−22%». Ноль остаётся без знака. */
export function formatDropRatio(ratio: number): string {
  const percent = Math.round(Math.abs(ratio) * 100);
  if (percent === 0) return ratio === 0 ? '0%' : `${MINUS}1%`;
  return `${MINUS}${percent}%`;
}

/**
 * 0.04 → «+4%». Симметричен formatDropRatio.
 *
 * Контракт §3.4: `rise_ratio` приходит ПОЛОЖИТЕЛЬНОЙ долей, как и `drop_ratio`;
 * направление задаётся тем, какое из двух полей не-null. Сервер гарантирует,
 * что одновременно заполнено не больше одного.
 */
export function formatRiseRatio(ratio: number): string {
  const percent = Math.round(Math.abs(ratio) * 100);
  if (percent === 0) return ratio === 0 ? '0%' : '+1%';
  return `+${percent}%`;
}

/** Модуль доли без знака — там, где направление уже выражено словами. */
export function formatRatioMagnitude(ratio: number): string {
  return `${Math.round(Math.abs(ratio) * 100)}%`;
}

/**
 * Направленная дельта поездки одним вызовом.
 *
 * Контракт §3.4 гарантирует: не-null ровно одно из drop_ratio/rise_ratio, либо
 * оба null (истории не хватило — направление неизвестно). ОБА не-null —
 * невозможное состояние: одна и та же цена не может одновременно упасть и
 * вырасти. Молча выбрать одно поле — значит спрятать рассинхрон данных, поэтому
 * инвариант падает громко (виден в dev и тестах), а не гадает (Global Constraint 8).
 */
export function formatChangeRatio(dropRatio: number | null, riseRatio: number | null): string {
  if (dropRatio !== null && riseRatio !== null) {
    throw new Error(
      `ChangeRatio: drop_ratio=${dropRatio} и rise_ratio=${riseRatio} одновременно не-null — ` +
        'нарушен инвариант контракта §3.4 (направление всегда ровно одно)',
    );
  }
  if (dropRatio !== null) return formatDropRatio(dropRatio);
  if (riseRatio !== null) return formatRiseRatio(riseRatio);
  return '—';
}

export function formatAge(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 60) return 'только что';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

/** Момент времени в зоне, которую отдаёт /api/meta.timezone (§9 спеки). */
export function formatObservedAt(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));

  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';

  return `${pick('day')}.${pick('month')} ${pick('hour')}:${pick('minute')}`;
}

/**
 * Только день и месяц момента времени — для подписи «цена от 25.07» (T39).
 * Зона обязательна: замер в 23:30 UTC в Москве относится уже к следующему дню.
 */
export function formatObservedDay(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(new Date(iso));

  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';

  return `${pick('day')}.${pick('month')}`;
}

/**
 * Полный момент с годом — для подписей, где важно «когда именно»:
 * время расчёта устаревшего вердикта (Д-Ф12). Без года подпись бесполезна:
 * вердикт может быть посчитан задолго до открытия карточки.
 */
export function formatDateTime(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    year: 'numeric',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));

  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';

  return `${pick('day')}.${pick('month')}.${pick('year')}, ${pick('hour')}:${pick('minute')}`;
}

/**
 * Календарная дата из date-only либо полного ISO-момента.
 *
 * `YYYY-MM-DD` нельзя прогонять через Date: UTC-полночь в зоне UTC−N стала бы
 * предыдущим днём. Полный timestamp, наоборот, обязан учитывать указанную
 * зону. Раньше timestamp попадал в date-only ветку, `day` становился строкой
 * `30T…`, а интерфейс печатал `NaN.08.2026` на оси графика.
 */
export function formatDate(iso: string, timezone: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    return `${dateOnly[3]}.${dateOnly[2]}.${dateOnly[1]}`;
  }

  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    year: 'numeric',
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(moment);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';
  return `${pick('day')}.${pick('month')}.${pick('year')}`;
}

export function formatNights(nights: number | null): string {
  if (nights === null || !Number.isFinite(nights)) return '—';
  const mod100 = nights % 100;
  const mod10 = nights % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${nights} ночей`;
  if (mod10 === 1) return `${nights} ночь`;
  if (mod10 >= 2 && mod10 <= 4) return `${nights} ночи`;
  return `${nights} ночей`;
}

/**
 * Русское числительное: «1 сравнение», «2 сравнения», «57 сравнений».
 *
 * 🔴 Заведено после живого показа T26: экран печатал «57 сравнений» рядом с
 * «31 сравнений» и «1 подтверждены двумя источниками». Число рядом с выводом о
 * деньгах — часть доказательства, и небрежная форма подрывает доверие к нему
 * ровно так же, как неверная цифра.
 */
export function formatPlural(
  value: number,
  forms: [one: string, few: string, many: string],
): string {
  const mod100 = Math.abs(value) % 100;
  const mod10 = Math.abs(value) % 10;
  const word =
    mod100 >= 11 && mod100 <= 14
      ? forms[2]
      : mod10 === 1
        ? forms[0]
        : mod10 >= 2 && mod10 <= 4
          ? forms[1]
          : forms[2];
  return `${formatCount(value)} ${word}`;
}

export function formatStars(stars: number | null): string {
  return stars === null || !Number.isFinite(stars) ? '' : `${stars}★`;
}

/** Человеческая подпись питания при разных кодах и написаниях источников. */
export function formatBoard(board: string | null): string {
  const value = board?.trim();
  if (!value) return 'Питание уточняется';
  const upper = value.toLocaleUpperCase('ru-RU');
  if (upper.startsWith('UAI') || upper.includes('УЛЬТРА')) return 'Ультра всё включено';
  if (
    upper === 'AI' ||
    upper.startsWith('AI ') ||
    upper.includes('ALL INCLUSIVE') ||
    upper.includes('ВСЁ ВКЛЮЧЕНО') ||
    upper.includes('ВСЕ ВКЛЮЧЕНО')
  ) return 'Всё включено';
  if (upper === 'FB' || upper.startsWith('FB ') || upper.includes('ПОЛНЫЙ ПАНСИОН')) {
    return 'Полный пансион';
  }
  if (upper === 'HB' || upper.startsWith('HB ') || upper.includes('ПОЛУПАНСИОН')) {
    return 'Полупансион';
  }
  if (
    upper === 'BB' ||
    upper.startsWith('BB ') ||
    upper.includes('ТОЛЬКО ЗАВТРАК') ||
    upper === 'ЗАВТРАК'
  ) return 'Завтрак';
  if (upper === 'RO' || upper.startsWith('RO ') || upper.includes('БЕЗ ПИТАНИЯ')) {
    return 'Без питания';
  }
  return value;
}


/**
 * Счётчик с разделителем разрядов. Отдельная функция, потому что
 * `toLocaleString('ru-RU')` ставит НЕРАЗРЫВНЫЙ пробел: он невидим в коде,
 * ломает поиск по странице и попадается в тестах как «не тот пробел».
 */
export function formatCount(value: number): string {
  return value.toLocaleString('ru-RU').replace(/\u00a0/g, ' ');
}
